// ASK-142 R2 — PRODUCT PICTURES STREAM IN; THE SHOP DOES NOT WAIT FOR THEM.
//
// Measured on the deployed plane (ASK-142 §4.1, `lost-boys-comics`, 67 products, iPhone 15 emulation):
// the loader cleared only when the LAST of 138 cover photos arrived — 103 s and 212 s on two runs —
// because the seat loop awaited each seat's covers, and each seat awaited each slot, in turn: median
// gap between one photo finishing and the next starting 1 ms, overlaps 0, at most 2 in flight. 83 %
// of the wait was pictures fetched one at a time.
//
// Every streaming engine shows the world and streams the textures in (Unreal texture streaming,
// Unity mipmap streaming): geometry seats at once in the slot's fallback colour, the covers fill in.
// This module is the streaming half:
//   · a bounded POOL of in-flight fetches (6 — the HTTP/1.1 per-host connection count, and a fair
//     share of an H2 connection on a phone radio) — never one-at-a-time, never 138 at once;
//   · VISIBLE-FIRST: the next job out of the queue is the one nearest the camera at the moment a slot
//     frees, not the one enqueued first — what streaming engines call priority by screen relevance;
//   · OFF-THREAD DECODE: `ImageBitmapLoader` (`fetch` → `createImageBitmap`, decoded off the main
//     thread) wherever the browser supports it, with exactly GLTFLoader's own capability rule and
//     options, so a cover binds with the same `flipY = false` glTF convention (ASK-129) on either path;
//   · an UPLOAD BUDGET: a decoded picture reaches the GPU (`upload`, the stage's `renderer.initTexture`)
//     at most `uploadsPerFrame` (1) per animation frame — Unreal's texture-streaming pool has the same
//     per-frame cap — so six covers landing together are spread over frames instead of one hitch;
//   · STALL-BOUNDED REQUESTS: a fetch that stops RECEIVING BYTES for `stallMs` is aborted and retried
//     once. It is an idle timeout, not a total one — measured 2026-10-14 on a slow link, headers came in
//     1–2 s and an 800 KB body took 10–20 s while still progressing; a total timeout aborted those
//     healthy downloads and restarted them from zero, which is the wrong trade on a phone radio. Only a
//     request that has genuinely stopped gives its slot back (the HTTP-client read-timeout rule);
//   · a `settled()` promise so instruments (probes, captures) can still wait for "every cover bound".

import * as THREE from "../../vendor/three.module.js";

export const COVER_STREAM_CONCURRENCY = 6;
export const COVER_FETCH_STALL_MS = 12000;     // no byte for this long → abort + retry
export const COVER_IMG_TIMEOUT_MS = 60000;      // the <img> fallback cannot see progress: a total bound
export const COVER_FETCH_RETRIES = 1;

/** GLTFLoader's own rule (three r183 `GLTFParser`): ImageBitmap where createImageBitmap honours its
 *  options — not Safari < 17, not Firefox < 98. Same rule, so embedded and feed textures agree. */
export function supportsImageBitmapTextures(nav = globalThis.navigator, hasCreateImageBitmap = typeof globalThis.createImageBitmap === "function") {
	if (!hasCreateImageBitmap) return false;
	const ua = nav?.userAgent || "";
	const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
	const safariVersion = isSafari ? Number((ua.match(/Version\/(\d+)/) || [])[1] ?? -1) : -1;
	if (isSafari && safariVersion < 17) return false;
	const ff = ua.match(/Firefox\/([0-9]+)\./);
	if (ff && Number(ff[1]) < 98) return false;
	return true;
}

/** A queue with a bounded number of running jobs; the next job is the lowest `priority()` at the
 *  moment a slot frees (re-evaluated then, because the camera moves while the queue drains). */
export function createJobPool({ concurrency = COVER_STREAM_CONCURRENCY } = {}) {
	const queue = [];
	let running = 0, maxRunning = 0, started = 0;
	const pump = () => {
		while (running < concurrency && queue.length) {
			let best = -1, bestP = Infinity;
			for (let i = 0; i < queue.length; i++) {
				let ready = true;
				try { ready = queue[i].ready ? !!queue[i].ready() : true; } catch { ready = true; }
				if (!ready) continue;   // ASK-142 §8.3 2 — not drawn yet (a closed box's covers): wait
				let p = 0;
				try { p = queue[i].priority ? Number(queue[i].priority()) : 0; } catch { p = 0; }
				if (!Number.isFinite(p)) p = 0;
				if (best < 0 || p < bestP) { bestP = p; best = i; }
			}
			if (best < 0) return;   // everything queued is waiting on visibility — `poke()` re-checks
			const job = queue.splice(best, 1)[0];
			running++; started++;
			if (running > maxRunning) maxRunning = running;
			Promise.resolve().then(job.run).then(job.resolve, job.reject).finally(() => { running--; pump(); });
		}
	};
	return {
		run(fn, priority = null, ready = null) {
			return new Promise((resolve, reject) => { queue.push({ run: fn, priority, ready, resolve, reject }); pump(); });
		},
		/** Re-check waiting jobs (call when visibility may have changed — a lid opened). */
		poke() { pump(); },
		/** Jobs waiting on visibility right now. */
		get waiting() { let n = 0; for (const j of queue) { try { if (j.ready && !j.ready()) n++; } catch { /* ready */ } } return n; },
		get queued() { return queue.length; },
		get running() { return running; },
		get stats() { return { queued: queue.length, running, maxRunning, started, concurrency }; },
	};
}

/**
 * The stage's cover stream: one image loader, one pool, one record of outstanding seat bindings.
 *   load(url, { priority })  → Promise<THREE.Texture>  (sRGB, flipY false; rejects on a failed load)
 *   track(promise)           → the promise, counted until it settles (a seat's whole cover binding)
 *   settled()                → resolves once nothing is queued, running or tracked
 */
export function createCoverStream({ concurrency = COVER_STREAM_CONCURRENCY, useImageBitmap = supportsImageBitmapTextures(), upload = null, uploadsPerFrame = 1, stallMs = COVER_FETCH_STALL_MS, imgTimeoutMs = COVER_IMG_TIMEOUT_MS, retries = COVER_FETCH_RETRIES, fetchImpl = globalThis.fetch?.bind(globalThis), ktx2Loader = null, maxRawSide = 0 } = {}) {
	const pool = createJobPool({ concurrency });
	const uploadSlot = createFrameBudget(uploadsPerFrame);
	const imgLoader = useImageBitmap ? null : new THREE.TextureLoader().setCrossOrigin("anonymous");
	let timeouts = 0, retried = 0;
	// The ImageBitmap path is three's ImageBitmapLoader inlined — same fetch (cors, same-origin
	// credentials) and the same createImageBitmap options — so each request can be aborted on its own
	// (`ImageBitmapLoader.abort()` aborts every request the loader holds).
	const isKtx2 = (url) => /\.ktx2(?:[?#]|$)/i.test(url);
	const fetchOnce = (url) => {
		// ASK-142 R1 — a cooked face (`media.derived.<face>.shelf|hero`, KTX2/Basis) is fetched with the
		// same stall bound and transcoded by the platform's ONE KTX2Loader (worker pool, GPU-native
		// format; it stays compressed on the GPU — the reason the cook exists).
		if (isKtx2(url)) {
			const loader = typeof ktx2Loader === "function" ? ktx2Loader() : ktx2Loader;
			if (!loader) return Promise.reject(new Error("cover-stream: no KTX2 loader"));
			const ctl = typeof AbortController === "function" ? new AbortController() : null;
			const idle = createIdleTimer(stallMs, () => ctl?.abort());
			const work = fetchImpl(url, { mode: "cors", credentials: "same-origin", ...(ctl ? { signal: ctl.signal } : {}) })
				.then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); idle.touch(); return readBodyWithProgress(r, idle.touch); })
				.then((blob) => blob.arrayBuffer())
				.then((buf) => { idle.stop(); return new Promise((res, rej) => loader.parse(buf, res, rej)); });
			return Promise.race([work, idle.expired]).finally(idle.stop);
		}
		if (!useImageBitmap) {
			return withTimeout(imgLoader.loadAsync(url), imgTimeoutMs, null);
		}
		const ctl = typeof AbortController === "function" ? new AbortController() : null;
		const idle = createIdleTimer(stallMs, () => ctl?.abort());
		const work = fetchImpl(url, { mode: "cors", credentials: "same-origin", ...(ctl ? { signal: ctl.signal } : {}) })
			.then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); idle.touch(); return readBodyWithProgress(r, idle.touch); })
			.then((blob) => decodeCapped(blob, maxRawSide));
		return Promise.race([work, idle.expired]).finally(idle.stop);
	};
	const fetchWithRetry = async (url, cancelled) => {
		for (let attempt = 0; ; attempt++) {
			if (cancelled?.()) throw new Error("cover-stream: cancelled");
			try { return await fetchOnce(url); }
			catch (err) {
				if (err?.coverTimeout) timeouts++;
				if (!err?.coverTimeout || attempt >= retries) throw err;
				retried++;
			}
		}
	};
	const pending = new Set();
	let waiters = [];
	// Settled = nothing running, and everything still queued is waiting on visibility (a closed box's
	// covers may never be drawn this visit — "settled" must not wait for them).
	const idleNow = () => !pool.running && pool.queued === pool.waiting && (pool.queued > 0 || !pending.size);
	const check = () => {
		if (!idleNow()) return;
		const w = waiters; waiters = [];
		for (const r of w) r();
	};
	const toTexture = (loaded, url) => {
		if (loaded?.isCompressedTexture) {   // KTX2: colour space from its DFD, orientation is the cook's (upright)
			loaded.name = url;
			return loaded;
		}
		const tex = useImageBitmap ? new THREE.Texture(loaded) : loaded;
		tex.name = url;   // an ImageBitmap carries no `src`; the name is how a texture says what it shows
		tex.colorSpace = THREE.SRGBColorSpace;
		tex.flipY = false;   // glTF's convention — and an ImageBitmap cannot be flipped at upload anyway
		tex.needsUpdate = true;
		return tex;
	};
	return {
		imageBitmap: useImageBitmap,
		load(url, { priority = null, cancelled = null, ready = null } = {}) {
			// A job still queued when the scene it was for has gone is dropped, not fetched.
			const p = pool.run(() => fetchWithRetry(url, cancelled).then((loaded) => toTexture(loaded, url)), priority, ready)
				.then(async (tex) => {
					if (!upload) return tex;
					await uploadSlot();
					try { upload(tex); } catch { /* the render loop uploads it on first draw instead */ }
					return tex;
				});
			pending.add(p);
			p.catch(() => {}).finally(() => { pending.delete(p); check(); });
			return p;
		},
		track(promise) {
			pending.add(promise);
			Promise.resolve(promise).catch(() => {}).finally(() => { pending.delete(promise); check(); });
			return promise;
		},
		poke() { pool.poke(); check(); },
		settled() {
			if (idleNow()) return Promise.resolve();
			return new Promise((r) => waiters.push(r));
		},
		get pending() { return pending.size; },
		get stats() { return { ...pool.stats, waiting: pool.waiting, pending: pending.size, imageBitmap: useImageBitmap, timeouts, retried }; },
	};
}

/** A per-animation-frame budget: `await take()` resolves at most `perFrame` times per frame (without
 *  requestAnimationFrame — Node, a hidden tab's throttle aside — it resolves at once). */
export function createFrameBudget(perFrame = 2, raf = globalThis.requestAnimationFrame?.bind(globalThis)) {
	const waiting = [];
	let scheduled = false;
	const frame = () => {
		scheduled = false;
		for (let i = 0; i < perFrame && waiting.length; i++) waiting.shift()();
		if (waiting.length) { scheduled = true; raf(frame); }
	};
	return function take() {
		if (!raf) return Promise.resolve();
		return new Promise((r) => {
			waiting.push(r);
			if (!scheduled) { scheduled = true; raf(frame); }
		});
	};
}

/** Resolve/reject with `promise`, or reject with `{ coverTimeout: true }` after `ms` (calling `onTimeout`,
 *  e.g. an AbortController's abort, so the stalled request releases its connection). */
export function withTimeout(promise, ms, onTimeout) {
	if (!(ms > 0)) return promise;
	let timer;
	const t = new Promise((_, reject) => {
		timer = setTimeout(() => {
			try { onTimeout?.(); } catch { /* the reject below is the signal */ }
			reject(Object.assign(new Error(`cover-stream: timed out after ${ms} ms`), { coverTimeout: true }));
		}, ms);
	});
	return Promise.race([promise, t]).finally(() => clearTimeout(timer));
}

/** An idle timer: `expired` rejects with `{ coverTimeout: true }` once `ms` pass without a `touch()`. */
export function createIdleTimer(ms, onExpire) {
	let timer = null, rejectFn = null, stopped = false;
	const expired = new Promise((_, reject) => { rejectFn = reject; });
	expired.catch(() => {});
	const arm = () => {
		if (stopped || !(ms > 0)) return;
		clearTimeout(timer);
		timer = setTimeout(() => {
			try { onExpire?.(); } catch { /* the reject below is the signal */ }
			rejectFn(Object.assign(new Error(`cover-stream: no bytes for ${ms} ms`), { coverTimeout: true }));
		}, ms);
	};
	arm();
	return { expired, touch: arm, stop: () => { stopped = true; clearTimeout(timer); } };
}

/** Read a Response body chunk by chunk, calling `onProgress` per chunk, into a Blob. */
export async function readBodyWithProgress(response, onProgress) {
	if (!response.body?.getReader) return response.blob();
	const reader = response.body.getReader(), parts = [];
	for (;;) {
		const { done, value } = await reader.read();
		if (done) break;
		parts.push(value);
		onProgress?.();
	}
	return new Blob(parts, { type: response.headers?.get?.("content-type") || "" });
}

/** Decode off the main thread; when `maxSide` > 0 and the picture is larger, resize it there too
 *  (ASK-142 §14.2 — the RAW fallback for a face with no cooked level obeys the tier's texture limit:
 *  a phone never uploads a 1000×1500 camera JPEG because one listing is still cooking). */
export async function decodeCapped(blob, maxSide = 0) {
	const opts = { premultiplyAlpha: "none", colorSpaceConversion: "none" };
	const bmp = await createImageBitmap(blob, opts);
	const w = bmp.width, h = bmp.height, long = Math.max(w, h);
	if (!(maxSide > 0) || long <= maxSide) return bmp;
	const k = maxSide / long;
	try {
		const small = await createImageBitmap(bmp, { ...opts, resizeWidth: Math.max(1, Math.round(w * k)), resizeHeight: Math.max(1, Math.round(h * k)), resizeQuality: "high" });
		bmp.close?.();
		return small;
	} catch {
		return bmp;   // a browser without resize options draws the full picture rather than none
	}
}
